import dayjs, { type Dayjs } from "dayjs"

const TIME = "h:mm A"
const TIME_NO_MERIDIEM = "h:mm"

const read = (value?: string | null): Dayjs | null => {
  if (!value) return null
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.tz() : null
}

/**
 * "Today, 4:15 – 5:15 AM", in the account's zone (dayjs's default, set by
 * `DayjsProvider`) and the account's date format for any other day. A range
 * that shares its meridiem states it once; one that crosses midnight names
 * both days.
 */
export function formatEventWindow(
  startAt: string | null | undefined,
  endAt: string | null | undefined,
  { dateFormat, today }: { dateFormat: string; today: string }
): string | null {
  const start = read(startAt)
  if (!start) return null
  const end = read(endAt)

  const dayOf = (moment: Dayjs) =>
    moment.isSame(dayjs().tz(), "day") ? today : moment.format(dateFormat)

  if (!end || !end.isAfter(start)) {
    return `${dayOf(start)}, ${start.format(TIME)}`
  }
  if (!end.isSame(start, "day")) {
    return `${dayOf(start)}, ${start.format(TIME)} – ${dayOf(end)}, ${end.format(TIME)}`
  }
  const startTime =
    start.format("A") === end.format("A")
      ? start.format(TIME_NO_MERIDIEM)
      : start.format(TIME)
  return `${dayOf(start)}, ${startTime} – ${end.format(TIME)}`
}

export type StartsIn =
  | { unit: "minutes"; count: number }
  | { unit: "hours"; count: number }

/**
 * How far off an event's start is, or null once it has started (never a
 * negative countdown) or when it is a day or more away, where the date row
 * already says enough. Minutes round up, so a reminder sent a few seconds
 * late for a 5-minute warning still reads "5 min".
 */
export function startsIn(
  startAt: string | null | undefined,
  now: number = Date.now()
): StartsIn | null {
  if (!startAt) return null
  const start = dayjs(startAt)
  if (!start.isValid()) return null
  const ms = start.valueOf() - now
  if (ms <= 0) return null
  const minutes = Math.ceil(ms / 60_000)
  if (minutes < 60) return { unit: "minutes", count: minutes }
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return { unit: "hours", count: hours }
  return null
}
